import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ListChecks, Pause, Play, Plus, Trash } from "@phosphor-icons/react";
import api from "@/lib/api";
import { TYPE_META } from "@/components/delivery/destinationConstants";
import { buildConfig, targetDisplay } from "@/components/delivery/destinationHelpers";
import { LeadShareDialog } from "@/components/pageSettings/LeadShareDialog";
import { ExtraPlaceForm, blankPlace } from "./ExtraPlaceForm";
import { DestinationTestButton } from "./DestinationTestButton";
import { SetupInstructionsButton } from "./SetupInstructionsButton";

const BTN = "border border-black px-2 py-1 text-[10px] uppercase tracking-widest inline-flex items-center gap-1 transition-colors duration-100 hover:bg-black hover:text-white disabled:opacity-40";
const errText = (e, fallback) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : fallback);
const TypeChip = ({ type }) => <span className="border border-black px-1.5 py-0.5 text-[10px] uppercase tracking-widest">{TYPE_META[type]?.label || type}</span>;

function CopyRow({ c, onChanged, onFields }) {
  const [busy, setBusy] = useState(false);
  const run = async (fn, ok) => {
    setBusy(true);
    try {
      await fn();
      toast.success(ok);
      onChanged();
    } catch (e) {
      toast.error(errText(e, "Couldn't update this place"));
    } finally {
      setBusy(false);
    }
  };
  const patch = (body, ok) => run(() => api.patch(`/admin/delivery-copies/${c.id}`, body), ok);
  const remove = () => {
    if (window.confirm("Stop sending leads to this place?")) run(() => api.delete(`/admin/delivery-copies/${c.id}`), "Extra place removed");
  };
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-zinc-200 px-3 py-2 text-xs" data-testid={`delivery-copy-${c.id}`}>
      <span className="label-mono">Also</span>
      <TypeChip type={c.type} />
      <span className="w-full min-w-0 break-all font-mono text-[11px] sm:w-auto sm:flex-1" data-testid={`delivery-copy-target-${c.id}`}>{targetDisplay(c)}</span>
      {!c.is_active && <span className="text-[10px] uppercase text-amber-800" data-testid={`delivery-copy-paused-${c.id}`}>Paused</span>}
      <label className="flex items-center gap-1 text-[11px]">
        <input type="checkbox" checked={!c.own_field_list} disabled={busy} data-testid={`delivery-copy-same-${c.id}`}
               onChange={(e) => patch({ own_field_list: !e.target.checked }, e.target.checked ? "Now sends the same fields as the main delivery" : "This place now has its own field list")} />
        Same fields as main
      </label>
      {c.own_field_list && (
        <button type="button" className={BTN} onClick={() => onFields(c)} data-testid={`delivery-copy-fields-${c.id}`}><ListChecks size={11} /> Fields</button>
      )}
      <button type="button" className={BTN} disabled={busy} onClick={() => patch({ is_active: !c.is_active }, c.is_active ? "Paused" : "Resumed")} data-testid={`delivery-copy-toggle-${c.id}`}>
        {c.is_active ? <Pause size={11} /> : <Play size={11} />} {c.is_active ? "Pause" : "Resume"}
      </button>
      <button type="button" className={BTN} disabled={busy} onClick={remove} aria-label="Remove this place" data-testid={`delivery-copy-remove-${c.id}`}><Trash size={11} /></button>
      <DestinationTestButton dest={c} />
      {c.own_field_list && <SetupInstructionsButton destId={c.id} />}
    </div>
  );
}

// A buyer's main delivery plus its extra places.
export function DeliveryCopies({ main, copies, max, onChanged }) {
  const [adding, setAdding] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fieldsFor, setFieldsFor] = useState(null);
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/destinations/${main.id}/copies`, {
        type: adding.type, config: buildConfig({ type: adding.type, config: adding.config }), own_field_list: adding.own_field_list,
      });
      toast.success("Extra place added");
      setAdding(null);
      onChanged();
    } catch (e) {
      toast.error(errText(e, "Couldn't add the place — check the settings"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="border-2 border-black" data-testid={`delivery-place-${main.id}`}>
      <div className="flex flex-wrap items-center gap-2 border-b-2 border-black bg-zinc-100 px-3 py-2 text-xs">
        <b>{main.name}</b>
        <TypeChip type={main.type} />
        <span className="min-w-0 break-all font-mono text-[11px]">{targetDisplay(main)}</span>
        {!main.is_active && <span className="text-[10px] uppercase text-amber-800">Paused</span>}
        <span className="label-mono ml-auto">Main delivery</span>
        <DestinationTestButton dest={main} />
      </div>
      <div className="flex flex-wrap items-center gap-2 border-b border-zinc-200 px-3 py-2 text-xs" data-testid={`delivery-handoff-${main.id}`}>
        <span className="text-[11px] text-zinc-600 w-full sm:w-auto sm:flex-1">
          Pick the fields this buyer gets{copies.some((c) => !c.own_field_list) ? " (also used by the extra places below)" : ""}, then email their tech team how to accept leads.
        </span>
        <button type="button" className={BTN} onClick={() => setFieldsFor(main)} data-testid={`delivery-fields-${main.id}`}><ListChecks size={11} /> Fields they get</button>
        <SetupInstructionsButton destId={main.id} />
      </div>
      {copies.map((c) => <CopyRow key={c.id} c={c} onChanged={onChanged} onFields={setFieldsFor} />)}
      {!copies.length && !adding && <div className="px-3 py-2 text-[11px] text-zinc-500">Only the main delivery gets leads.</div>}
      <div className="p-3">
        {adding ? (
          <div className="space-y-2">
            <ExtraPlaceForm value={adding} onChange={setAdding} onRemove={() => setAdding(null)} testId={`delivery-copy-new-${main.id}`} />
            <button type="button" onClick={save} disabled={busy} className={`${BTN} bg-black text-white`} data-testid={`delivery-copy-save-${main.id}`}>
              {busy ? "Saving…" : "Save place"}
            </button>
          </div>
        ) : copies.length < max && (
          <button type="button" className={BTN} onClick={() => setAdding(blankPlace(main.type === "email" ? "" : main.config?.to || ""))} data-testid={`delivery-copy-add-${main.id}`}>
            <Plus size={11} /> Also send to another place
          </button>
        )}
      </div>
      {fieldsFor && <LeadShareDialog destId={fieldsFor.id} title={`${fieldsFor.name} — lead fields sent`} onClose={() => setFieldsFor(null)} />}
    </div>
  );
}

export function BuyerDeliveryPlaces({ orgId }) {
  const [d, setD] = useState(null);
  const load = useCallback(() => {
    api.get(`/admin/clients/${orgId}/delivery-places`).then((r) => setD(r.data)).catch((e) => {
      toast.error(errText(e, "Couldn't load delivery places"));
      setD({ places: [], max: 2 });
    });
  }, [orgId]);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;
  return (
    <div className="space-y-3" data-testid="buyer-delivery-places">
      <p className="text-[11px] text-zinc-600">
        Every lead the main delivery gets also goes to each extra place (up to {d.max}), like their webhook and an email inbox.
        The buyer is billed once, and the lead counts as delivered when the main delivery gets it.
      </p>
      {d.places.length ? d.places.map((p) => <DeliveryCopies key={p.main.id} main={p.main} copies={p.copies} max={d.max} onChanged={load} />) : (
        <div className="text-[11px] text-zinc-500" data-testid="buyer-delivery-places-empty">This buyer has no delivery set up yet.</div>
      )}
    </div>
  );
}
